import type { ProductStock } from '@shared/models/Product';
import type { RelocationModel, RelocationStatus } from '@modules/Relocations/List/models/Relocation';

/** Pasos del flujo de escaneo */
export type RelocationStep = 'cart' | 'location' | 'product' | 'quantity';

/** Carrito resuelto tras escanearlo */
export interface ResolvedCart {
  id: number;
  code: string;
}

/** Entrada de GET /locations → stock_batches[] (origen para batch_id al registrar línea) */
export interface LocationStockBatch {
  batch_id: number;
  product_id: number;
  quantity?: number;
  batch?: {
    batch_number?: string | null;
    expiry_date?: string | null;
  } | null;
}

/** Ubicación resuelta tras escanearla */
export interface ResolvedLocation {
  id: number;
  code: string;
  /** Si la API lo envía (picking con un solo producto asignado) */
  product_id?: number | null;
  /** Stock total cuando la API lo envía como número (p. ej. listado de ubicaciones) */
  stockTotal?: number | null;
  /** Stock por lote según ubicación escaneada (necesario para POST .../lines con batch_id) */
  stock_batches?: LocationStockBatch[];
  /**
   * Productos en la ubicación: `product` embebido y/o ids deduplicados desde `stock_batches`
   * para mostrar vista previa mientras el operario escanea el producto.
   */
  locationProducts?: ResolvedProduct[];
}

/** True si la ubicación tiene inventario movible (lotes, producto asignado o stock > 0). */
export function resolvedLocationHasInventory(loc: ResolvedLocation): boolean {
  if (loc.stock_batches && loc.stock_batches.length > 0) return true;
  if (loc.locationProducts && loc.locationProducts.length > 0) return true;
  if (loc.product_id != null && loc.product_id > 0) return true;
  if (typeof loc.stockTotal === 'number' && Number.isFinite(loc.stockTotal) && loc.stockTotal > 0) return true;
  return false;
}

/** Producto resuelto tras escanearlo (forma mínima usada en el flujo) */
export interface ResolvedProduct {
  id: number;
  code?: string;
  name?: string;
  image?: string;
}

/** Iguala el código leído con los productos ya resueltos en la ubicación (sin nueva llamada a API). */
export function resolveProductScanFromLocation(location: ResolvedLocation, rawCode: string): ResolvedProduct | null {
  const trimmed = rawCode.trim();
  if (!trimmed) return null;

  const candidates = location.locationProducts ?? [];
  if (candidates.length === 0) return null;

  const normalized = trimmed.toLowerCase();

  const byCode = candidates.find((p) => {
    const c = p.code;
    return c != null && String(c).trim().toLowerCase() === normalized;
  });
  if (byCode) return byCode;

  const asNum = Number(trimmed);
  if (Number.isInteger(asNum) && asNum > 0) {
    const byId = candidates.find((p) => p.id === asNum);
    if (byId) return byId;
  }

  return null;
}

/** Línea ya registrada en la reubicación */
export interface RelocationLine {
  id: number;
  relocation_id?: number;
  product_id: number;
  location_id?: number | null;
  from_location_id?: number | null;
  qty: number;
  batch_id?: number | null;
  /** Metadatos del lote cuando el API los devuelve embebidos */
  batch?: {
    id?: number;
    batch_number?: string | null;
    expiry_date?: string | null;
  } | null;
  created_at?: string;
  updated_at?: string;
  /** Algunos backends embed la ubicación (code) */
  location?: { id: number; code: string } | null;
  /** Algunos backends embed el producto */
  product?: {
    id: number;
    name?: string;
    code?: string;
    image?: string;
    image_hash?: string;
  } | null;
}

/** Totales acumulados por producto devueltos por el backend */
export interface RelocationTotal {
  product_id: number;
  qty: number;
}

/** Detalle completo devuelto por GET /relocations/:id */
export interface RelocationDetail extends RelocationModel {
  lines?: RelocationLine[];
  totals?: RelocationTotal[];
  /** Algunos backends devuelven el carrito embebido */
  cart?: { id: number; code: string } | null;
}

export type { RelocationStatus };
export interface ProductDetail {
  id: number;
  name: string;
  description?: string;
  code?: string;
  cost?: number;
  status?: string;
  team_id?: number;
  category_id?: number;
  image_hash?: string;
  batch_active?: boolean;
  batch_type?: string | null;
  stock?: ProductStock;
}

/** Elige batch_id para un producto a partir de GET /locations (stock_batches). Si hay varios lotes, usa el de mayor cantidad. */
export function resolveBatchIdForProduct(
  stock_batches: LocationStockBatch[] | undefined,
  productId: number,
): number | undefined {
  if (!stock_batches?.length) return undefined;
  const matching = stock_batches.filter((s) => s.product_id === productId);
  if (matching.length === 0) return undefined;
  if (matching.length === 1) return matching[0].batch_id;
  return [...matching].sort((a, b) => (b.quantity ?? 0) - (a.quantity ?? 0))[0].batch_id;
}
